import React, { useState } from 'react';
import { Wand2, Volume2, Clock, Bot } from 'lucide-react';
import Badge from '../../components/ui/Badge';

export function ScriptEditorSection({
  script = {},
  onChangeScript,
  onGenerateScript,
  onGenerateAudio,
  isGeneratingScript = false,
  isGeneratingAudio = false,
  onNext
}) {
  const [selectedProvider, setSelectedProvider] = useState('gemini'); // 'gemini' | 'openai' | 'template'
  const [targetDuration, setTargetDuration] = useState(30); // 30 | 45 | 60

  // Combine all script sections into single editable text or sync with object
  const getFullText = () => {
    if (script.fullText) return script.fullText;
    const parts = [];
    if (script.hook?.narration) parts.push(script.hook.narration);
    if (script.sections?.length) {
      script.sections.forEach(s => {
        if (s.narration) parts.push(s.narration);
      });
    }
    if (script.conclusion?.narration) parts.push(script.conclusion.narration);
    if (script.cta?.narration) parts.push(script.cta.narration);
    return parts.join('\n\n');
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    
    onChangeScript({
      ...script,
      fullText: text,
      hook: { ...(script.hook || {}), narration: paragraphs[0] || '' },
      conclusion: { ...(script.conclusion || {}), narration: paragraphs[paragraphs.length - 2] || '' },
      cta: { ...(script.cta || {}), narration: paragraphs[paragraphs.length - 1] || '' }
    });
  };

  const handleTriggerGenerate = () => {
    if (onGenerateScript) {
      onGenerateScript({
        provider: selectedProvider,
        targetDuration: targetDuration
      });
    }
  };

  const defaultSampleScript = `Bạn đang phân vân giữa lựa chọn A và B? Đây là bản phân tích nhanh từ Dược sĩ.

Với sản phẩm A bên trái, điểm mạnh là thành phần tự nhiên, dịu nhẹ và phù hợp da nhạy cảm.
Trong khi đó sản phẩm B bên phải nổi bật ở hiệu quả nhanh, tác dụng mạnh mẽ và giá thành tối ưu.

Lời khuyên: Nếu bạn ưu tiên sự an toàn lâu dài hãy chọn A, cần giải quyết tức thì hãy chọn B.

Lưu ngay video để áp dụng đúng cách bạn nhé!`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            BƯỚC 2: KỊCH BẢN & LỜI BÌNH
          </span>
          <h4 style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-main)', marginTop: '1px' }}>
            Script video so sánh
          </h4>
        </div>
        <Badge variant={script.generatedBy?.includes('gemini') ? 'info' : script.generatedBy?.includes('openai') ? 'success' : 'outline'} size="sm">
          {script.generatedBy || 'Sẵn sàng sinh AI'}
        </Badge>
      </div>

      {/* AI Provider & Duration Selection Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '8px', background: 'var(--bg-subtle)', padding: '8px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
        {/* Provider Selector */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Bot size={11} color="var(--primary)" /> Engine AI:
          </span>
          <select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            style={{
              padding: '5px 8px',
              fontSize: '11px',
              fontWeight: 700,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-color)',
              background: '#FFFFFF',
              color: 'var(--text-main)',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value="gemini">Google Gemini AI</option>
            <option value="openai">OpenAI ChatGPT (GPT-4o)</option>
            <option value="template">Mẫu chuẩn (Fallback)</option>
          </select>
        </div>

        {/* Duration Selector */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={11} color="#EA580C" /> Thời lượng:
          </span>
          <select
            value={targetDuration}
            onChange={(e) => setTargetDuration(parseInt(e.target.value, 10))}
            style={{
              padding: '5px 8px',
              fontSize: '11px',
              fontWeight: 700,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-color)',
              background: '#FFFFFF',
              color: 'var(--text-main)',
              outline: 'none',
              cursor: 'pointer'
            }}
          >
            <option value={30}>~30 Giây (Ngắn gọn)</option>
            <option value={45}>~45 Giây (Chuẩn)</option>
            <option value={60}>~60 Giây (Chi tiết)</option>
          </select>
        </div>
      </div>

      {/* Script Multi-Paragraph Textarea */}
      <textarea
        rows={5}
        value={script.fullText !== undefined ? script.fullText : (getFullText() || defaultSampleScript)}
        onChange={handleTextChange}
        placeholder="Nhập kịch bản so sánh..."
        style={{
          width: '100%',
          padding: '12px',
          borderRadius: 'var(--radius-md)',
          background: '#FFFFFF',
          border: '1.5px solid var(--border-color)',
          color: 'var(--text-main)',
          fontSize: '13px',
          lineHeight: 1.5,
          fontFamily: 'inherit',
          resize: 'vertical',
          outline: 'none',
          boxShadow: 'var(--shadow-sm)'
        }}
      />

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleTriggerGenerate}
          disabled={isGeneratingScript}
          style={{
            flex: 1,
            padding: '10px 14px',
            background: 'var(--primary-gradient)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            color: '#FFFFFF',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <Wand2 size={14} />
          <span>{isGeneratingScript ? 'Đang sinh AI...' : `Sinh kịch bản ${selectedProvider === 'openai' ? 'OpenAI' : selectedProvider === 'gemini' ? 'Gemini' : 'AI'} (~${targetDuration}s)`}</span>
        </button>

        <button
          type="button"
          onClick={onGenerateAudio}
          disabled={isGeneratingAudio}
          style={{
            flex: 1,
            padding: '10px 14px',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-main)',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <Volume2 size={14} color="var(--primary)" />
          <span>{isGeneratingAudio ? 'Đang tạo...' : 'Tạo giọng đọc AI'}</span>
        </button>
      </div>
    </div>
  );
}

export default ScriptEditorSection;
